"use client";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";
type AccentColor = "violet" | "pink" | "cyan" | "emerald" | "amber" | "red" | "blue" | "indigo";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  accentColor: AccentColor;
  setAccentColor: (color: AccentColor) => void;
  resolvedTheme: "dark" | "light";
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

const accentColors: Record<AccentColor, { primary: string; accent: string }> = {
  violet: { primary: "139 92 246", accent: "168 85 247" },
  pink: { primary: "236 72 153", accent: "244 114 182" },
  cyan: { primary: "6 182 212", accent: "34 211 238" },
  emerald: { primary: "16 185 129", accent: "52 211 153" },
  amber: { primary: "245 158 11", accent: "251 191 36" },
  red: { primary: "239 68 68", accent: "248 113 113" },
  blue: { primary: "59 130 246", accent: "96 165 250" },
  indigo: { primary: "99 102 241", accent: "129 140 248" },
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");
  const [accentColor, setAccentColorState] = useState<AccentColor>("violet");
  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">("dark");

  useEffect(() => {
    // Load saved preferences with validation
    const savedTheme = localStorage.getItem("nexus-theme") as Theme | null;
    const savedAccent = localStorage.getItem("nexus-accent");

    if (savedTheme && ["dark", "light", "system"].includes(savedTheme)) {
      setThemeState(savedTheme);
    }
    if (savedAccent && savedAccent in accentColors) {
      setAccentColorState(savedAccent as AccentColor);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Resolve theme
    let resolved: "dark" | "light" = "dark";
    if (theme === "system") {
      resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } else {
      resolved = theme;
    }
    setResolvedTheme(resolved);

    // Apply theme class
    root.classList.remove("light", "dark");
    root.classList.add(resolved);

    // Apply accent color - fallback to violet if invalid
    const colors = accentColors[accentColor] || accentColors.violet;
    root.style.setProperty("--primary", colors.primary);
    root.style.setProperty("--accent", colors.accent);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === "system") {
        const newResolved = e.matches ? "dark" : "light";
        setResolvedTheme(newResolved);
        root.classList.remove("light", "dark");
        root.classList.add(newResolved);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, accentColor]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("nexus-theme", newTheme);
  };

  const setAccentColor = (newColor: AccentColor) => {
    setAccentColorState(newColor);
    localStorage.setItem("nexus-accent", newColor);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, accentColor, setAccentColor, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Theme selector component
export function ThemeSelector() {
  const { theme, setTheme, accentColor, setAccentColor } = useTheme();

  const themes: { id: Theme; label: string }[] = [
    { id: "dark", label: "Dark" },
    { id: "light", label: "Light" },
    { id: "system", label: "System" },
  ];

  const accents: { id: AccentColor; color: string; label: string }[] = [
    { id: "violet", color: "#8b5cf6", label: "Violet" },
    { id: "pink", color: "#ec4899", label: "Pink" },
    { id: "cyan", color: "#06b6d4", label: "Cyan" },
    { id: "emerald", color: "#10b981", label: "Emerald" },
    { id: "amber", color: "#f59e0b", label: "Amber" },
    { id: "red", color: "#ef4444", label: "Red" },
  ];

  return (
    <div className="space-y-6">
      {/* Theme Selection */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Theme</h3>
        <div className="grid grid-cols-3 gap-3">
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === t.id
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div
                className={`w-full h-12 rounded-lg mb-2 ${
                  t.id === "dark"
                    ? "bg-zinc-900"
                    : t.id === "light"
                    ? "bg-zinc-100"
                    : "bg-gradient-to-r from-zinc-900 to-zinc-100"
                }`}
              />
              <span className="text-sm font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Accent Color Selection */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Accent Color</h3>
        <div className="flex gap-3">
          {accents.map((a) => (
            <button
              key={a.id}
              onClick={() => setAccentColor(a.id)}
              className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${
                accentColor === a.id ? "ring-2 ring-white ring-offset-2 ring-offset-card" : ""
              }`}
              style={{ backgroundColor: a.color }}
              title={a.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
